import React from "react";
import Box from "@mui/material/Box";
import { FormItemWrap } from "./formItem.styled";
import * as PropTypes from "prop-types";

// FormItem used to render an antd <Col>. It now renders a plain Box with the
// stable class `form-item-col` (stylesheets that used to target `.ant-col`
// target this class instead). antd's 24-column grid props are still accepted:
// `span`/`offset` and the responsive xs..xxl spans become percentage widths.
const GRID_COLUMNS = 24;
const toPercent = (cols) => `${(cols / GRID_COLUMNS) * 100}%`;

// antd breakpoint name -> MUI breakpoint name (MUI has no xxl).
const RESPONSIVE_KEYS = {
  xs: "xs",
  sm: "sm",
  md: "md",
  lg: "lg",
  xl: "xl",
  xxl: "xl",
};

const spanStyles = (span) =>
  span === 0
    ? { display: "none" }
    : { flex: `0 0 ${toPercent(span)}`, maxWidth: toPercent(span) };

const FormItem = ({
  label,
  children,
  optional,
  span = undefined,
  offset = undefined,
  className = undefined,
  ...otherProps
}) => {
  const domProps = {};
  const sx = [{ position: "relative", maxWidth: "100%", minHeight: "1px" }];
  if (typeof span === "number") sx.push(spanStyles(span));
  if (typeof offset === "number" && offset > 0) {
    sx.push({ marginLeft: toPercent(offset) });
  }
  Object.entries(otherProps).forEach(([key, value]) => {
    if (!(key in RESPONSIVE_KEYS)) {
      domProps[key] = value;
      return;
    }
    const responsiveSpan =
      typeof value === "number" ? value : value && value.span;
    if (typeof responsiveSpan === "number") {
      sx.push((theme) => ({
        [theme.breakpoints.up(RESPONSIVE_KEYS[key])]: spanStyles(responsiveSpan),
      }));
    }
  });

  return (
    <Box
      className={["form-item-col", className].filter(Boolean).join(" ")}
      sx={sx}
      {...domProps}
    >
      <FormItemWrap>
        <div className="top-wrap">
          {label && <label>{label}</label>}
          {optional && <span className="optional">optional</span>}
        </div>
        {children}
      </FormItemWrap>
    </Box>
  );
};

FormItem.propTypes = {
  label: PropTypes.string,
  optional: PropTypes.bool,
  span: PropTypes.number,
  offset: PropTypes.number,
  className: PropTypes.string,
  children: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node),
  ]),
};

FormItem.defaultProps = {
  label: null,
  optional: null,
  children: null,
};

export default FormItem;
export { FormItem };
